import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Anchor, Button, Container, Group, SegmentedControl, Select, Skeleton, Stack } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconArrowLeft } from '@tabler/icons-react';
import { bestPriceService } from '@/api/services/BestPriceService';
import { orderService } from '@/api/services/OrderService';
import { isServiceUnavailable } from '@/api/errors';
import { useAgencyAgreementMissed, useSaleTree } from '@/hooks/useSell';
import { useProfile } from '@/hooks/useProfile';
import { useRequiredData } from '@/hooks/useRequiredData';
import { flattenSaleTree, groupSellByBasis } from '@/lib/sellTree';
import { formatOrgName, formatProperty, formatPropertyCondition } from '@/lib/format';
import { useAuth } from '@/stores/authContext';
import { ROUTES } from '@/routes/paths';
import BasisPicker from '@/components/common/BasisPicker';
import BulletinSection from '@/components/common/BulletinSection';
import PageTitle from '@/components/common/PageTitle';
import FormMessage from '@/components/auth/FormMessage';
import BestPriceResultsTable, { type BestPriceResultRow } from '@/components/app/bestPrice/BestPriceResultsTable';
import SearchCriteriaLine from '@/components/app/bestPrice/SearchCriteriaLine';
import type { BasisValue } from '@/lib/basisPicker';
import { useIsAgent } from '@/hooks/useRoles';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import AgencyGateBanner from '@/components/app/sell/AgencyGateBanner';
import SellAttestationGate from '@/components/app/sell/SellAttestationGate';
import type { SellBestPrice } from '@/types/sell';
import classes from './SellBestPriceSearchPage.module.css';

/** Показываем не больше этого числа лучших цен. Запрашиваем на одну больше: так видно, что за пределом есть ещё. */
const SHOW_LIMIT = 20;

type Prepayment = 'ANY' | 'PERCENT_0' | 'PERCENT_100';
type SearchField = 'basis' | 'goods' | 'quality';

const FIELD_ID: Readonly<Record<SearchField, string>> = {
  basis: 'sell-search-basis',
  goods: 'sell-search-goods',
  quality: 'sell-search-quality',
};

const FIELD_LABEL: Readonly<Record<SearchField, string>> = {
  basis: 'app.sell.search.fieldBasis',
  goods: 'app.sell.search.fieldGoods',
  quality: 'app.sell.search.fieldQuality',
};

export interface Criteria {
  readonly address: string;
  readonly addressTitle: string;
  readonly goodsType: string;
  readonly propertySetId: number;
  readonly prepayment: Prepayment;
}

/** Выполненный поиск: критерии и номер попытки — повторное «Найти» с теми же критериями тоже обновляет выдачу. */
interface Submitted extends Criteria {
  readonly attempt: number;
  /** Качество подписью на момент поиска: справочник качеств в форме может уже относиться к другой культуре. */
  readonly qualityText: string;
}

const sameCriteria = (a: Criteria, b: Criteria): boolean =>
  a.address === b.address &&
  a.addressTitle === b.addressTitle &&
  a.goodsType === b.goodsType &&
  a.propertySetId === b.propertySetId &&
  a.prepayment === b.prepayment;

/**
 * Ссылка «Продать» (свободный режим, без своего предложения): продавец заполнит логистику на `/app/sell/counter`.
 * Предоплата — та, при которой получена цена строки: при фильтре «Любая» строка может прийти и со 100 %.
 */
export function sellDealUrl(item: SellBestPrice, criteria: Criteria): string {
  const prepayment = item.prepaymentType ?? (criteria.prepayment === 'ANY' ? 'PERCENT_0' : criteria.prepayment);
  const qs = new URLSearchParams({
    purchaseOrderId: String(item.purchaseOrderId),
    purchaseOrderVersion: String(item.purchaseOrderVersion),
    fromAddress: criteria.address,
    fromAddressTitle: criteria.addressTitle,
    prepayment: prepayment === 'PERCENT_100' ? 'PERCENT_100' : 'PERCENT_0',
    goods: item.goodsType ?? criteria.goodsType,
  });
  return `${ROUTES.APP_SELL_COUNTER}?${qs.toString()}`;
}

function focusField(field: SearchField): void {
  const element = document.getElementById(FIELD_ID[field]);
  // Качество недоступно, пока грузятся его варианты: тогда только показываем поле.
  if (element instanceof HTMLInputElement && !element.disabled) {
    element.focus();
  }
  element?.scrollIntoView({ block: 'center' });
}

// «Лучшие цены покупателей» раздела «Продать» («Бюллетень»): лучшие цены покупки под товар продавца, пересчитанные
// к его складу (FCA). Форма одной строкой без рамки, выдача таблицей; лучшая цена — первая строка. Агенту поиск
// доступен справочно: без кнопки «Продать» и со ссылкой назад на «Рынок».
export default function SellBestPriceSearchPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent();
  const agencyMissed = useAgencyAgreementMissed();
  // Агента аттестация не касается: он пользуется поиском справочно.
  const attestationBlocked = useAttestationRequired() && !isAgent;
  // Пока профиль и обязательные данные не загружены, ограничения неизвестны: форму не показываем, иначе она мелькнула
  // бы перед сообщением об ограничении.
  const profile = useProfile();
  const requiredData = useRequiredData();
  const gatesKnown = (profile.data != null || profile.isError) && (requiredData.data != null || requiredData.isError);
  const allowed = gatesKnown && !attestationBlocked && !agencyMissed;

  const [basis, setBasis] = useState<BasisValue | null>(null);
  const [prefillDone, setPrefillDone] = useState(false);
  const [goodsType, setGoodsType] = useState<string | null>(null);
  const [propertySetId, setPropertySetId] = useState<string | null>(null);
  const [prepayment, setPrepayment] = useState<Prepayment>('ANY');
  const [attempted, setAttempted] = useState(false);
  const [submitted, setSubmitted] = useState<Submitted | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Свои базисы — из дерева продаж. Если базис у продавца один, он подставляется один раз, до показа формы: поле
  // не перемонтируется и не теряет ввод, а обновление дерева потом подставленное не трогает.
  const tree = useSaleTree();
  const treeKnown = isAgent || tree.data != null || tree.isError;
  const ownBases = useMemo(
    () => groupSellByBasis(flattenSaleTree(tree.data)).map((base) => ({ address: base.address, addressTitle: base.addressTitle })),
    [tree.data],
  );
  if (!prefillDone && treeKnown) {
    // Обновление состояния во время отрисовки — допустимый в React способ вывести значение из загруженных данных.
    setPrefillDone(true);
    if (!isAgent && ownBases.length === 1) {
      setBasis(ownBases[0]!);
    }
  }

  const goodsClasses = useQuery({
    queryKey: ['goodsClasses'] as const,
    queryFn: ({ signal }) => orderService.getGoodsClasses(signal),
    enabled: isAuthenticated && allowed,
    staleTime: 10 * 60_000,
  });
  const propertySets = useQuery({
    queryKey: ['goodsPropertySets', goodsType] as const,
    queryFn: ({ signal }) => orderService.getGoodsPropertySets(goodsType!, signal),
    enabled: isAuthenticated && !!goodsType,
    staleTime: 10 * 60_000,
  });

  // В ключе — критерии и номер попытки (без подписей): повторное «Найти» с теми же критериями загружает выдачу заново,
  // а таблица на это время остаётся прежней, а не сменяется скелетом.
  const searchKey = submitted
    ? {
        address: submitted.address,
        addressTitle: submitted.addressTitle,
        goodsType: submitted.goodsType,
        propertySetId: submitted.propertySetId,
        prepayment: submitted.prepayment,
        attempt: submitted.attempt,
      }
    : null;
  const results = useQuery({
    queryKey: ['sellBestPriceSearch', searchKey] as const,
    queryFn: ({ signal }) =>
      bestPriceService.getSellForAddress(
        {
          address: submitted!.address,
          goodsType: submitted!.goodsType,
          propertySetId: submitted!.propertySetId,
          prepaymentTypes: submitted!.prepayment === 'ANY' ? ['PERCENT_0', 'PERCENT_100'] : [submitted!.prepayment],
          limit: SHOW_LIMIT + 1,
          offset: 0,
        },
        signal,
      ),
    enabled: isAuthenticated && !!submitted,
    staleTime: 30_000,
    retry: false,
    placeholderData: (previous, previousQuery) => {
      const previousKey = previousQuery?.queryKey[1] as Criteria | null | undefined;
      return previousKey && submitted && sameCriteria(previousKey, submitted) ? previous : undefined;
    },
  });

  const mainPropertyName = propertySets.data?.[0]?.properties?.[0]?.name;
  const propertyData = useMemo(
    () =>
      (propertySets.data ?? [])
        .filter((set) => set.propsSetId != null && set.properties?.[0]?.value != null)
        .map((set) => ({ value: String(set.propsSetId), label: formatPropertyCondition(set.properties![0]!) })),
    [propertySets.data],
  );
  const qualityLabel = propertyData.find((option) => option.value === propertySetId)?.label;

  const missing: SearchField[] = [
    ...(basis ? [] : (['basis'] as const)),
    ...(goodsType ? [] : (['goods'] as const)),
    ...(propertySetId ? [] : (['quality'] as const)),
  ];
  const current: Criteria | null =
    basis && goodsType && propertySetId
      ? { address: basis.address, addressTitle: basis.addressTitle, goodsType, propertySetId: Number(propertySetId), prepayment }
      : null;
  const stale = submitted != null && (current == null || !sameCriteria(current, submitted));

  const search = (event: FormEvent): void => {
    event.preventDefault();
    if (!current) {
      setAttempted(true);
      focusField(missing[0]!);
      return;
    }
    setAttempted(false);
    const qualityText = [mainPropertyName, qualityLabel].filter(Boolean).join(' ');
    setSubmitted((previous) => ({ ...current, qualityText, attempt: (previous?.attempt ?? 0) + 1 }));
    // Фокус — на выдачу: кнопка «Найти» на время поиска неактивна, и фокус иначе ушёл бы в начало страницы.
    requestAnimationFrame(() => resultsRef.current?.focus());
  };

  const prepaymentText = (type?: string): string | undefined =>
    type === 'PERCENT_0' ? t('app.sell.prepayment0Short') : type === 'PERCENT_100' ? t('app.sell.search.prepayment100') : undefined;
  const criteriaText = submitted
    ? [
        formatOrgName(submitted.addressTitle) ?? submitted.address,
        submitted.goodsType,
        submitted.qualityText,
        submitted.prepayment === 'ANY' ? t('app.bestPrice.results.anyPrepayment') : prepaymentText(submitted.prepayment),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const allItems = results.data ?? [];
  const hasMore = allItems.length > SHOW_LIMIT;
  const items = allItems.slice(0, SHOW_LIMIT);
  const rows: BestPriceResultRow[] = submitted
    ? items.map((item) => ({
        key: `${item.purchaseOrderId}-${item.purchaseOrderVersion}-${item.prepaymentType ?? ''}`,
        party: formatOrgName(item.buyerName) ?? '—',
        inn: item.buyerInn,
        place: formatOrgName(item.buyerAddressTitle) ?? item.buyerAddress,
        quality: item.propertyName
          ? formatProperty({ name: item.propertyName, value: item.propertyValue != null ? String(item.propertyValue) : undefined, postfix: item.propertyPostfix })
          : undefined,
        prepayment: prepaymentText(item.prepaymentType),
        price: item.pricePerTon,
        // Агент смотрит справочно, а по устаревшей выдаче сделку не открываем: критерии в форме уже другие.
        to: isAgent || stale ? undefined : sellDealUrl(item, submitted),
      }))
    : [];

  const fieldError = (field: SearchField): string | undefined =>
    attempted && missing.includes(field) ? t('auth.validation.required') : undefined;

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Stack gap="sm">
          <Anchor
            component={Link}
            to={isAgent ? ROUTES.APP_AGENT_DASHBOARD : ROUTES.APP_SELL}
            c="gray.7"
            fz="sm"
            underline="never"
            style={{ alignSelf: 'flex-start' }}
          >
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} aria-hidden />
              {t(isAgent ? 'app.sell.search.toMarket' : 'app.sell.search.toOffers')}
            </Group>
          </Anchor>
          <div>
            <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
              {t('app.sell.search.title')}
            </PageTitle>
            <p className={classes.lead}>{t('app.sell.search.lead')}</p>
          </div>
        </Stack>

        {/* Аттестация — базовое требование, проверяем её раньше агентского договора. */}
        {!gatesKnown || !treeKnown ? (
          <Skeleton h={76} />
        ) : attestationBlocked ? (
          <SellAttestationGate />
        ) : agencyMissed ? (
          <AgencyGateBanner />
        ) : (
          <>
            <form onSubmit={search} noValidate>
              <div className={classes.form}>
                <div className={classes.basis}>
                  <BasisPicker
                    id={FIELD_ID.basis}
                    value={basis}
                    onChange={setBasis}
                    ownBases={isAgent ? [] : ownBases}
                    label={t('app.sell.search.address')}
                    placeholder={t(isAgent ? 'app.sell.search.addressPlaceholderAgent' : 'app.sell.search.addressPlaceholder')}
                    error={fieldError('basis')}
                    required
                  />
                </div>
                <Select
                  id={FIELD_ID.goods}
                  label={t('app.sell.search.goods')}
                  placeholder={t('app.sell.search.goodsPlaceholder')}
                  data={goodsClasses.data ?? []}
                  value={goodsType}
                  onChange={(value) => {
                    setGoodsType(value);
                    setPropertySetId(null);
                  }}
                  searchable
                  required
                  error={
                    goodsClasses.isError ? (
                      <ReloadError text={t('app.sell.search.goodsError')} onRetry={() => goodsClasses.refetch()} />
                    ) : (
                      fieldError('goods')
                    )
                  }
                />
                <Select
                  id={FIELD_ID.quality}
                  label={mainPropertyName ?? t('app.sell.search.quality')}
                  placeholder={goodsType ? t('app.sell.search.qualityPlaceholder') : t('app.sell.search.qualityFirst')}
                  data={propertyData}
                  value={propertySetId}
                  onChange={setPropertySetId}
                  disabled={!goodsType || propertySets.isLoading}
                  nothingFoundMessage={t('app.sell.search.qualityNone')}
                  required
                  error={
                    propertySets.isError ? (
                      <ReloadError text={t('app.sell.search.qualityError')} onRetry={() => propertySets.refetch()} />
                    ) : (
                      fieldError('quality')
                    )
                  }
                />
                <fieldset className={classes.prepayment}>
                  <legend>{t('app.sell.search.prepayment')}</legend>
                  <SegmentedControl
                    value={prepayment}
                    onChange={(value) => setPrepayment(value as Prepayment)}
                    size="md"
                    data={[
                      { value: 'ANY', label: t('app.sell.search.prepaymentAny') },
                      { value: 'PERCENT_0', label: t('app.sell.prepayment0Short') },
                      { value: 'PERCENT_100', label: t('app.sell.search.prepayment100') },
                    ]}
                  />
                </fieldset>
                <Button type="submit" loading={results.isFetching}>
                  {t('app.sell.search.find')}
                </Button>
              </div>
              {missing.length > 0 && !attempted ? (
                <p className={classes.remaining}>
                  {t('app.sell.search.remaining', { fields: missing.map((field) => t(FIELD_LABEL[field])).join(', ') })}
                </p>
              ) : null}
            </form>

            {submitted ? (
              <div ref={resultsRef} tabIndex={-1} style={{ outline: 'none' }}>
                {/* Число найденных — в строке «Найдено N» под заголовком, у заголовка его не повторяем. */}
                <BulletinSection title={t('app.sell.search.section')} spaced>
                  <Stack gap="sm">
                    {/* Строка состояния смонтирована всё время, пока есть поиск: так программа чтения с экрана объявляет
                        и результат, и устаревание выдачи. */}
                    <SearchCriteriaLine
                      criteria={criteriaText}
                      count={results.data ? rows.length : null}
                      stale={stale}
                    />
                    {results.isError ? (
                      <FormMessage>
                        <Group gap="sm">
                          <span>{isServiceUnavailable(results.error) ? t('app.sell.search.unavailable') : t('app.sell.search.failed')}</span>
                          {/* После изменения критериев повтор искал бы по прежним: тогда только «Найти». */}
                          {stale ? null : (
                            <Button variant="default" size="compact-md" loading={results.isFetching} onClick={() => results.refetch()}>
                              {t('common.retry')}
                            </Button>
                          )}
                        </Group>
                      </FormMessage>
                    ) : results.isLoading ? (
                      <Stack gap={6} aria-busy="true">
                        {[0, 1, 2, 3, 4].map((index) => (
                          <Skeleton key={index} h={44} />
                        ))}
                      </Stack>
                    ) : rows.length === 0 ? (
                      <p className={classes.note}>{t('app.sell.search.emptyText')}</p>
                    ) : (
                      <BestPriceResultsTable
                        rows={rows}
                        action="sell"
                        withAction={!isAgent}
                        labels={{
                          party: t('app.sell.search.colBuyer'),
                          place: t('app.sell.search.colPlace'),
                          quality: t('app.sell.search.colQuality'),
                          prepayment: t('app.sell.search.colPrepayment'),
                          price: t('app.sell.search.colPrice'),
                        }}
                      />
                    )}
                    {hasMore ? <p className={classes.note}>{t('app.bestPrice.results.limit', { count: SHOW_LIMIT })}</p> : null}
                  </Stack>
                </BulletinSection>
              </div>
            ) : null}
          </>
        )}
      </Stack>
    </Container>
  );
}

/** Ошибка загрузки справочника у поля: текст и повтор загрузки. */
function ReloadError({ text, onRetry }: { readonly text: string; readonly onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <span>
      {text}{' '}
      <Anchor component="button" type="button" fz="inherit" c="inherit" underline="always" onClick={onRetry}>
        {t('common.retry')}
      </Anchor>
    </span>
  );
}
